import { useCallback, useEffect, useState } from "react";

import { api, clinicHeader, readError } from "../api/client";
import { ErrorNote } from "./ErrorNote";

type Note = {
  id: string;
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
  author_name: string;
  written_at: string;
  released: boolean;
  addenda?: Note[];
};

type Visit = {
  id: string;
  class: string;
  status: string;
  period_start: string;
  period_end: string | null;
  primary_performer_name: string;
  reason_text: string;
  closed_automatically: boolean;
  note?: Note | null;
  records?: { id: string; type: string; payload: Record<string, unknown> }[];
};

const FIELDS = [
  ["subjective", "Subjective", "What the patient reports."],
  ["objective", "Objective", "What you found on examination."],
  ["assessment", "Assessment", "What you think it is."],
  ["plan", "Plan", "What happens next."],
] as const;

function when(iso: string) {
  return new Date(iso).toLocaleString("en-MY", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * The visits on a chart, newest first (CLN-F-064).
 *
 * Before this existed, a vital sign and a diagnosis both attached to the
 * patient and to nothing else, so a reader saw a list of facts with no visit
 * that grouped them. Each visit here carries its note and whatever a clinician
 * recorded while it was open.
 *
 * Only a doctor opens a visit, writes its note, or closes it (CLN-F-060,
 * CLN-F-065). The server checks the same rule, so the buttons being absent is
 * a convenience rather than the enforcement.
 */
export function VisitPanel({
  patientId,
  isDoctor,
  onChanged,
}: {
  patientId: string;
  isDoctor: boolean;
  onChanged: () => void;
}) {
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [addendumTo, setAddendumTo] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: failed } = await api.GET("/portal/patients/{id}/encounters", {
      params: { path: { id: patientId }, header: clinicHeader() },
    });
    setLoading(false);

    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setError(null);
    setVisits((data?.items ?? []) as Visit[]);
  }, [patientId]);

  useEffect(() => {
    void load();
  }, [load]);

  const open = async () => {
    const { error: failed } = await api.POST("/portal/patients/{id}/encounters", {
      params: { path: { id: patientId }, header: clinicHeader() },
      body: { class: "ambulatory" },
    });
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    await load();
    onChanged();
  };

  const close = async (visitId: string) => {
    const { error: failed } = await api.POST("/portal/encounters/{id}/close", {
      params: { path: { id: visitId }, header: clinicHeader() },
    });
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    await load();
    onChanged();
  };

  const current = visits.find((v) => v.status === "in_progress");

  if (loading) return <p className="empty">Reading the visits…</p>;

  return (
    <section className="visits">
      <div className="visits-head">
        <h2>Visits</h2>
        {isDoctor && !current && (
          <button type="button" onClick={open}>
            Start a visit
          </button>
        )}
        {isDoctor && current && (
          <button type="button" onClick={() => close(current.id)}>
            Close this visit
          </button>
        )}
      </div>

      <ErrorNote message={error} />

      {current && (
        <p className="hint">
          A visit is open. Anything you record now belongs to it, with no step to remember.
        </p>
      )}

      {visits.length === 0 && <p className="empty">No visits recorded yet.</p>}

      {visits.map((visit) => (
        <article key={visit.id} className={`visit ${visit.status === "in_progress" ? "visit-open" : ""}`}>
          <header>
            <strong>{when(visit.period_start)}</strong>
            <span className="muted">
              {" · "}
              {visit.primary_performer_name}
              {visit.class === "emergency" && " · Emergency"}
              {visit.status === "in_progress" && " · Open"}
              {visit.closed_automatically && " · Closed automatically"}
            </span>
            {visit.reason_text && <p className="muted">{visit.reason_text}</p>}
          </header>

          {visit.records && visit.records.length > 0 && (
            <ul className="visit-records">
              {visit.records.map((record) => (
                <li key={record.id}>
                  <span className="tag">{record.type}</span>{" "}
                  {String(
                    record.payload?.display ??
                      record.payload?.medication_text ??
                      record.payload?.title ??
                      "",
                  )}
                </li>
              ))}
            </ul>
          )}

          {visit.note && editing !== visit.id && (
            <NoteBody
              note={visit.note}
              isDoctor={isDoctor}
              onRewrite={() => setEditing(visit.id)}
              onAddendum={() => setAddendumTo(visit.note!.id)}
            />
          )}

          {!visit.note && editing !== visit.id && (
            <p className="empty">
              No note for this visit.
              {isDoctor && (
                <>
                  {" "}
                  <button type="button" className="linklike" onClick={() => setEditing(visit.id)}>
                    Write one
                  </button>
                </>
              )}
            </p>
          )}

          {editing === visit.id && (
            <NoteForm
              encounterId={visit.id}
              existing={visit.note ?? null}
              onDone={() => {
                setEditing(null);
                void load();
              }}
              onCancel={() => setEditing(null)}
            />
          )}

          {addendumTo && visit.note?.id === addendumTo && (
            <NoteForm
              noteId={addendumTo}
              addendum
              onDone={() => {
                setAddendumTo(null);
                void load();
              }}
              onCancel={() => setAddendumTo(null)}
            />
          )}
        </article>
      ))}
    </section>
  );
}

function NoteBody({
  note,
  isDoctor,
  onRewrite,
  onAddendum,
}: {
  note: Note;
  isDoctor: boolean;
  onRewrite: () => void;
  onAddendum: () => void;
}) {
  return (
    <div className="note">
      {FIELDS.map(([key, label]) =>
        note[key] ? (
          <p key={key}>
            <span className="note-label">{label}</span>
            {note[key]}
          </p>
        ) : null,
      )}

      <p className="muted note-by">
        {note.author_name} · {when(note.written_at)}
        {!note.released && " · Not released to the patient"}
      </p>

      {(note.addenda ?? []).map((addendum) => (
        <div className="note addendum" key={addendum.id}>
          <span className="note-label">Addendum</span>
          {FIELDS.map(([key]) => (addendum[key] ? <p key={key}>{addendum[key]}</p> : null))}
          <p className="muted note-by">
            {addendum.author_name} · {when(addendum.written_at)}
          </p>
        </div>
      ))}

      {isDoctor && (
        <div className="row-actions">
          <button type="button" onClick={onAddendum}>
            Add to this note
          </button>
          <button type="button" onClick={onRewrite}>
            Correct it
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Writing a note, or adding to one.
 *
 * Correcting does not edit. It writes a new note that supersedes the old one,
 * and the old text stays readable (CLN-F-066). An addendum leaves the original
 * active and hangs beneath it (CLN-F-067). The form says which it is doing,
 * because a doctor should know that the first version survives either way.
 */
function NoteForm({
  encounterId,
  noteId,
  existing,
  addendum,
  onDone,
  onCancel,
}: {
  encounterId?: string;
  noteId?: string;
  existing?: Note | null;
  addendum?: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [values, setValues] = useState({
    subjective: addendum ? "" : (existing?.subjective ?? ""),
    objective: addendum ? "" : (existing?.objective ?? ""),
    assessment: addendum ? "" : (existing?.assessment ?? ""),
    plan: addendum ? "" : (existing?.plan ?? ""),
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const empty = !Object.values(values).some((v) => v.trim());

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);

    const { error: failed } = addendum
      ? await api.POST("/portal/notes/{id}/addendum", {
          params: { path: { id: noteId! }, header: clinicHeader() },
          body: values,
        })
      : await api.POST("/portal/encounters/{id}/note", {
          params: { path: { id: encounterId! }, header: clinicHeader() },
          body: values,
        });
    setSaving(false);

    if (failed) {
      setError(readError(failed).message);
      return;
    }
    onDone();
  };

  return (
    <form className="record-form note-form" onSubmit={submit}>
      {existing && !addendum && (
        <p className="hint">
          This writes a correction. The version you are replacing stays readable in the chart.
        </p>
      )}
      {addendum && (
        <p className="hint">
          This adds to the note. The note itself is unchanged and stays as it is.
        </p>
      )}

      {FIELDS.map(([key, label, help]) => (
        <label key={key}>
          {label} <span className="optional">{help}</span>
          <textarea
            rows={key === "plan" || key === "assessment" ? 2 : 3}
            value={values[key]}
            onChange={(e) => setValues({ ...values, [key]: e.target.value })}
          />
        </label>
      ))}

      <ErrorNote message={error} />

      <div className="row-actions">
        <button type="submit" disabled={saving || empty}>
          {saving ? "Saving…" : addendum ? "Add to the note" : existing ? "Save the correction" : "Save the note"}
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
